import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CommonButton from "components/commonButton/CommonButton";
import { toast } from "react-toastify";
import requestInstance from "services/Request";
import { API_ENDPOINT } from "services/Api";

const env = process.env.REACT_APP_B2B_API_BASE_URL;

const EditChargesDialog = ({ open, handleClose, tripId, tollCharges: initialToll, parkingCharges: initialParking, garageToPickup: initialGarageToPickup, dropToGarage: initialDropToGarage, onSave }) => {
  const [tollCharges, setTollCharges] = useState(initialToll ?? 0);
  const [parkingCharges, setParkingCharges] = useState(initialParking ?? 0);
  const [garageToPickup, setGarageToPickup] = useState(initialGarageToPickup ?? 0);
  const [dropToGarage, setDropToGarage] = useState(initialDropToGarage ?? 0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setTollCharges(initialToll ?? 0);
      setParkingCharges(initialParking ?? 0);
      setGarageToPickup(initialGarageToPickup ?? 0);
      setDropToGarage(initialDropToGarage ?? 0);
    }
  }, [open, initialToll, initialParking, initialGarageToPickup, initialDropToGarage]);

  const handleSave = async () => {
    const tollChanged = String(tollCharges) !== String(initialToll ?? 0);
    const parkingChanged = String(parkingCharges) !== String(initialParking ?? 0);
    const garageToPickupChanged = String(garageToPickup) !== String(initialGarageToPickup ?? 0);
    const dropToGarageChanged = String(dropToGarage) !== String(initialDropToGarage ?? 0);

    if (!tollChanged && !parkingChanged && !garageToPickupChanged && !dropToGarageChanged) {
      toast.info("No changes to update");
      return;
    }

    setSaving(true);
    try {
      const response = await requestInstance.patch(
        `${env}${API_ENDPOINT.reservation.manageTripChargesApi}/${tripId}`,
        {
          tollCharges: Number(tollCharges) || 0,
          parkingCharges: Number(parkingCharges) || 0,
          kilometersOut: Number(garageToPickup) || 0,
          kilometersIn: Number(dropToGarage) || 0,
        }
      );

      toast.success(response?.data?.message || "Details updated successfully");
      if (onSave) onSave();
      handleClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update details");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="edit-dialog"
    >
      <DialogTitle className="flex justifyBetween alignCenter">
        <Typography variant="head5" className="fw600 colorBlack">
          Edit
        </Typography>
        <IconButton onClick={handleClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Toll Charges"
              type="number"
              fullWidth
              size="small"
              value={tollCharges}
              onChange={(e) => setTollCharges(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Parking Charges"
              type="number"
              fullWidth
              size="small"
              value={parkingCharges}
              onChange={(e) => setParkingCharges(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Garage to Pickup (KM)"
              type="number"
              fullWidth
              size="small"
              value={garageToPickup}
              onChange={(e) => setGarageToPickup(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Drop to Garage (KM)"
              type="number"
              fullWidth
              size="small"
              value={dropToGarage}
              onChange={(e) => setDropToGarage(e.target.value)}
            />
          </Grid>
        </Grid>

        <Grid container justifyContent="flex-end" className="mt20">
          <CommonButton
            name={saving ? "Updating..." : "Update"}
            btnClass="btnSm"
            onClick={handleSave}
            disabled={saving}
          />
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default EditChargesDialog;
